import {
    ActionIcon,
    Badge,
    BadgeProps,
    Button,
    ButtonProps,
    Group,
    Menu,
    MenuItemProps,
    Paper,
    PolymorphicComponentProps,
    Stack,
    Text,
    Title,
} from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons-react";
import { ReactNode } from "react";
import { formatIpawsChannel } from "shared/components/ipaws/format";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { arr } from "shared/utils/array";
import { titlecase } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { createSlots } from "shared/utils/slots";
import { zx } from "shared/utils/zx";
import { IpawsAlertThumbnailFragment, IpawsChannel } from "src/gql/graphql";

/** @private */
const IpawsAlertCardAction = (props: PolymorphicComponentProps<"button", MenuItemProps>) => <Menu.Item {...props} />;
/** @private */
const IpawsAlertCardPrimaryAction = (props: PolymorphicComponentProps<"button", ButtonProps>) => <Button variant="default" {...props} />;
/** @private */
const IpawsAlertCardBadge = (props: BadgeProps) => <Badge color="green" miw="fit-content" {...props} />;

export const IpawsAlertCard = Object.assign(
    (props: { data: IpawsAlertThumbnailFragment; children: ReactNode }) => {
        const slots = createSlots(props.children, {
            IpawsAlertCardAction,
            IpawsAlertCardPrimaryAction,
            IpawsAlertCardBadge,
        });

        const isMobile = useIsMobile();

        const info = props.data.capMessage.infos.find((it) => it.language == "en-US");
        const evtCode = props.data.certificate.channels.flatMap((ch) => ch.eventCodes).find((ec) => ec.id == info?.eventCodes.at(0));

        return (
            <Paper p="sm">
                <Stack>
                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                        <Stack gap="xs">
                            <Group wrap="nowrap" align="flex-start">
                                <Title order={3}>{maybe(evtCode)?.take((it) => `${it.id}: ${it.description}`) ?? "Unknown"}</Title>
                                {!isMobile && slots.IpawsAlertCardBadge}
                            </Group>
                            <Text size="xs">
                                {arr
                                    .ofNotNull(
                                        zx.date().nullable().parse(props.data.capMessage.sent)?.toLocaleString(),
                                        props.data.certificate.name,
                                        titlecase(props.data.certificate.type),
                                    )
                                    .interleave(" | ")
                                    .take()}
                            </Text>
                        </Stack>
                        <Group wrap="nowrap">
                            {slots.IpawsAlertCardPrimaryAction}
                            {slots.IpawsAlertCardAction.length > 0 && (
                                <Menu>
                                    <Menu.Target>
                                        <ActionIcon variant="transparent">
                                            <IconDotsVertical size={16} />
                                        </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown>{...slots.IpawsAlertCardAction}</Menu.Dropdown>
                                </Menu>
                            )}
                        </Group>
                    </Group>
                    <Stack gap="xs">
                        <Text size="sm" fw="bold">
                            Channels
                        </Text>
                        <Group gap="xs">
                            {arr(Object.values(IpawsChannel))
                                .except(...(info?.blockedChannels ?? Object.values(IpawsChannel)))
                                .take()
                                .map((chan) => (
                                    <Badge variant="light" color="gray" key={chan}>
                                        {formatIpawsChannel(chan)}
                                    </Badge>
                                ))}
                        </Group>
                    </Stack>
                    <Stack gap="xs">
                        <Text size="sm" fw="bold">
                            Headline
                        </Text>
                        <Text size="sm">{info?.headline ?? "Unknown"}</Text>
                    </Stack>
                    <Stack gap="xs">
                        <Text size="sm" fw="bold">
                            Description
                        </Text>
                        <Text size="sm">{info?.description ?? "Unknown"}</Text>
                    </Stack>
                </Stack>
            </Paper>
        );
    },
    {
        Action: IpawsAlertCardAction,
        PrimaryAction: IpawsAlertCardPrimaryAction,
        Badge: IpawsAlertCardBadge,
    },
);
